import React, { useLayoutEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

/**
 * The paper the clips are drawn on, top edge to bottom edge (sampled from
 * every clip). The player paints the whole screen with it so a clip reads
 * full-bleed on any screen shape.
 */
export const STORIES_SURROUND = 'linear-gradient(to bottom, #F7F4F8, #F4F1F4)';

/**
 * Wraps the (lazy) stories routes so the screen is the clips' paper and the
 * status bar text is dark from the very first frame, including while the
 * stories chunk is still loading. Kept out of the lazy chunk on purpose, so
 * the first frame never shows a mismatched loader or status bar.
 */
export const StoriesBackdrop: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Layout effect so the native call is queued before the first paint.
  useLayoutEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let previous: Style | undefined;
    let unmounted = false;
    // Read the current style first (bridge calls run in order), so the
    // switch to dark text is not held up waiting for the answer.
    StatusBar.getInfo()
      .then((info) => { if (!unmounted) previous = info.style; })
      .catch(() => undefined);
    // Style.Light = dark text, for the light paper.
    StatusBar.setStyle({ style: Style.Light }).catch(() => undefined);
    return () => {
      unmounted = true;
      StatusBar.setStyle({ style: previous ?? Style.Default }).catch(() => undefined);
    };
  }, []);

  return (
    <div
      className="fixed inset-0"
      style={{ background: STORIES_SURROUND }}
      data-cy="onboarding-stories-backdrop"
    >
      {children}
    </div>
  );
};

export default StoriesBackdrop;
